import { useEffect, useRef } from 'react';

// Minimal dialog accessibility: Escape closes, focus moves into the
// dialog on open, Tab cycles inside it, and focus returns to whatever
// opened it on close. Attach the returned ref to the dialog card and
// pair with role="dialog" aria-modal="true" on the overlay.
export const useDialog = (onClose) => {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const el = dialogRef.current;

    // Initial focus: an [autofocus] element if present, else the first
    // focusable thing in the dialog
    const focusTarget =
      el?.querySelector('[autofocus]') ||
      el?.querySelector('input, textarea, select, button, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key === 'Tab' && el) {
        const focusables = el.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  return dialogRef;
};
